import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MouseIcon from '@mui/icons-material/Mouse';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import StarIcon from '@mui/icons-material/Star';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { Avatar, Box, Chip, CircularProgress, Divider, Grid, LinearProgress, Stack, Theme, Tooltip, Typography } from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import dinero from 'dinero.js';
import moment from 'moment-timezone';
import 'moment/locale/en-gb';
import { useParams } from 'react-router-dom';
// import WarningIcon from '@mui/icons-material/Warning';
import OverviewCard from '../../../components/common/OverviewCard';
import PriceCard from '../../../components/common/PriceCard';
import { metricTypeName } from '../../../utils/common';
// import SextforceMetricsSuspiciousUsersList from '../../../components/services/sextforce/metrics/SextforceMetricsSuspiciousUsersList';
import React from 'react';
import SextforceMetricsCampaignCountersGraph from '../../../components/services/sextforce/metrics/campaigns/details/SextforceMetricsCampaignCountersGraph';
import SextforceMetricsCampaignSubscribedAtGraph from '../../../components/services/sextforce/metrics/campaigns/details/SextforceMetricsCampaignSubscribedAtGraph';
import SextforceMetricsTrialCountersGraph from '../../../components/services/sextforce/metrics/trials/details/SextforceMetricsTrialCountersGraph';
import SextforceMetricsTrialLink from '../../../components/services/sextforce/metrics/trials/details/SextforceMetricsTrialLink';
import SextforceMetricsTrialSubscribedAtGraph from '../../../components/services/sextforce/metrics/trials/details/SextforceMetricsTrialSubscribedAtGraph';
import useSextforcePublicMetricCounters from '../../../hooks/useSextforcePublicMetricCounters';
import useSextforcePulicMetricLiveStats from '../../../hooks/useSextforcePublicMetricLiveStats';
import useSextforcePublicMetricOverview, { OnlyFansMetricsPublicDataOverview } from '../../../hooks/useSextforcePublicMetricOverview';
import useSextforcePublicMetricSubscribedAt from '../../../hooks/useSextforcePublicMetricSubscribedAt';

const LiveChip = (
    <Tooltip title="Live updates every minute">
        <Chip
            variant="filled"
            label="Live"
            icon={<RssFeedIcon />}
            size="small"
            color="success"
            sx={{
                cursor: 'pointer',
            }}
        />
    </Tooltip>
);

const PublicMetricData = ({
    metricShareLinkId,
    metricOverviewLoading,
    metricOverview,
    timezone,
}: {
    metricShareLinkId: string;
    metricOverviewLoading: boolean;
    metricOverview?: OnlyFansMetricsPublicDataOverview | OnlyFansMetricsPublicDataOverview[];
    timezone: string;
}) => {
    const theme: Theme = useTheme();

    const {
        data: liveStatsSingle,
        isLoading: liveStatsSingleLoading,
        error: liveStatsError,
    } = useSextforcePulicMetricLiveStats(metricShareLinkId, Array.isArray(metricOverview) ? undefined : metricOverview?.metricId);

    // const { data: publicMetric, isLoading: metricLoading } = useSextforcePulicMetric(timezone, metricShareLinkId);

    const { data: publicMetricCounters, isLoading: metricCountersLoading } = useSextforcePublicMetricCounters(
        timezone,
        metricShareLinkId,
        Array.isArray(metricOverview) ? undefined : metricOverview?.metricId,
    );

    const { data: publicMetricSubscribedAt, isLoading: metricSubscribedAtLoading } = useSextforcePublicMetricSubscribedAt(
        timezone,
        metricShareLinkId,
        Array.isArray(metricOverview) ? undefined : metricOverview?.metricId,
    );

    return (
        <>
            {!Array.isArray(metricOverview) && (
                <Box>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <Avatar src={metricOverview?.avatar} sx={{ width: '48px', height: '48px', mr: 1 }}>
                            {metricOverview?.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Stack direction="column" spacing={0} flexGrow={1}>
                            <Typography variant="h5">
                                {metricOverview ? metricTypeName(metricOverview.type) : ''} Metrics for {metricOverview?.username}
                            </Typography>
                            {metricOverview?.name && <Typography variant="h6">{metricOverview?.name}</Typography>}
                        </Stack>
                    </Stack>

                    {metricOverview?.createdAt && (
                        <Typography variant="subtitle1">{`Created at: ${moment(metricOverview.createdAt)
                            .tz(timezone)
                            .format('LL')}`}</Typography>
                    )}

                    {(liveStatsError || !liveStatsSingle) && metricOverview && metricOverview.updatedAt && (
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color:
                                    moment(metricOverview.updatedAt).diff(moment(), 'days') < 0
                                        ? theme.palette.error.dark
                                        : theme.palette.success.dark,
                                marginBottom: 2,
                            }}
                        >
                            Updated at: {moment(metricOverview.updatedAt).format('LL hh:mm a')}
                        </Typography>
                    )}

                    {!liveStatsError && liveStatsSingle && (
                        <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
                            {LiveChip}
                            <Typography variant="subtitle1">
                                Updated at:{' '}
                                <span
                                    key={liveStatsSingle.updatedAt?.valueOf()}
                                    style={{
                                        // momentarily flash background on component render
                                        animation: 'backgroundRefresh .5s ease forwards',
                                    }}
                                >
                                    {liveStatsSingle.updatedAt ? moment(liveStatsSingle.updatedAt).format('LL hh:mm a') : 'unknown'}
                                </span>
                            </Typography>
                        </Stack>
                    )}

                    <Grid container spacing={1} flexGrow={0} justifyContent="center" sx={{ mb: 2, mt: 2 }}>
                        {metricOverview?.counters &&
                            'countSubscribers' in metricOverview.counters &&
                            typeof metricOverview.counters.countSubscribers === 'number' && (
                                <Grid item xs={12} md={6} lg={3}>
                                    <OverviewCard
                                        title={
                                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                <Typography variant="subtitle1" align="right">
                                                    Total Subscribed
                                                </Typography>
                                                {!liveStatsError && liveStatsSingle && LiveChip}
                                                {!liveStatsError && liveStatsSingleLoading && <CircularProgress size={16} />}
                                            </Stack>
                                        }
                                        value={
                                            <div>
                                                <Typography variant="h5" align="right">
                                                    <div
                                                        key={liveStatsSingle?.updatedAt?.valueOf()}
                                                        style={{
                                                            // momentarily flash background on component render
                                                            animation: 'backgroundRefresh .5s ease forwards',
                                                        }}
                                                    >
                                                        {liveStatsSingle?.counters.countSubscribers?.toLocaleString() ||
                                                            metricOverview?.counters.countSubscribers?.toLocaleString()}
                                                    </div>
                                                </Typography>
                                            </div>
                                        }
                                        loading={false}
                                        icon={<SubscriptionsIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                        theme={theme}
                                    />
                                </Grid>
                            )}
                        {metricOverview?.counters &&
                            'countTransitions' in metricOverview.counters &&
                            typeof metricOverview.counters.countTransitions === 'number' && (
                                <Grid item xs={12} md={6} lg={3}>
                                    <OverviewCard
                                        title={
                                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                <Typography variant="subtitle1" align="right">
                                                    Total Clicked
                                                </Typography>
                                                {!liveStatsError && liveStatsSingle && LiveChip}
                                                {!liveStatsError && liveStatsSingleLoading && <CircularProgress size={16} />}
                                            </Stack>
                                        }
                                        value={
                                            <div>
                                                <Typography variant="h5" align="right">
                                                    <div
                                                        key={liveStatsSingle?.updatedAt?.valueOf()}
                                                        style={{
                                                            // momentarily flash background on component render
                                                            animation: 'backgroundRefresh .5s ease forwards',
                                                        }}
                                                    >
                                                        {liveStatsSingle?.counters.countTransitions?.toLocaleString() ||
                                                            metricOverview?.counters.countTransitions?.toLocaleString()}
                                                    </div>
                                                </Typography>
                                            </div>
                                        }
                                        loading={false}
                                        icon={<MouseIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                        theme={theme}
                                    />
                                </Grid>
                            )}

                        {metricOverview?.counters &&
                            'claimsCount' in metricOverview.counters &&
                            typeof metricOverview.counters.claimsCount === 'number' && (
                                <Grid item xs={12} md={6} lg={3}>
                                    <OverviewCard
                                        title={
                                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                <Typography variant="subtitle1" align="right">
                                                    Total Claimed
                                                </Typography>
                                                {!liveStatsError && liveStatsSingle && LiveChip}
                                                {!liveStatsError && liveStatsSingleLoading && <CircularProgress size={16} />}
                                            </Stack>
                                        }
                                        value={
                                            <div>
                                                <Typography variant="h5" align="right">
                                                    <div
                                                        key={liveStatsSingle?.updatedAt?.valueOf()}
                                                        style={{
                                                            // momentarily flash background on component render
                                                            animation: 'backgroundRefresh .5s ease forwards',
                                                        }}
                                                    >
                                                        {liveStatsSingle?.counters.claimsCount?.toLocaleString() ||
                                                            metricOverview?.counters.claimsCount?.toLocaleString()}
                                                    </div>
                                                </Typography>
                                            </div>
                                        }
                                        loading={false}
                                        icon={<SubscriptionsIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                        theme={theme}
                                    />
                                </Grid>
                            )}
                        {metricOverview?.counters &&
                            'subscribeCounts' in metricOverview.counters &&
                            typeof metricOverview.counters.subscribeCounts === 'number' && (
                                <Grid item xs={12} md={6} lg={3}>
                                    <OverviewCard
                                        title={
                                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                <Typography variant="subtitle1" align="right">
                                                    Total Clicked
                                                </Typography>
                                                {!liveStatsError && liveStatsSingle && LiveChip}
                                                {!liveStatsError && liveStatsSingleLoading && <CircularProgress size={16} />}
                                            </Stack>
                                        }
                                        value={
                                            <div>
                                                <Typography variant="h5" align="right">
                                                    <div
                                                        key={liveStatsSingle?.updatedAt?.valueOf()}
                                                        style={{
                                                            // momentarily flash background on component render
                                                            animation: 'backgroundRefresh .5s ease forwards',
                                                        }}
                                                    >
                                                        {liveStatsSingle?.counters.subscribeCounts?.toLocaleString() ||
                                                            metricOverview?.counters.subscribeCounts?.toLocaleString()}
                                                    </div>
                                                </Typography>
                                            </div>
                                        }
                                        loading={false}
                                        icon={<MouseIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                        theme={theme}
                                    />
                                </Grid>
                            )}
                        {metricOverview?.counters &&
                            'spendersCount' in metricOverview.counters &&
                            typeof metricOverview.counters.spendersCount === 'number' && (
                                <Grid item xs={12} md={6} lg={3}>
                                    <OverviewCard
                                        title="Total Spenders"
                                        value={metricOverview?.counters.spendersCount?.toLocaleString()}
                                        loading={false}
                                        icon={<StarIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                        theme={theme}
                                    />
                                </Grid>
                            )}
                        {/* {metric.suspiciousUsers && (
                            <Grid item xs={12} md={6} lg={3}>
                                <OverviewCard
                                    title="Suspicious Users"
                                    value={metric.suspiciousUsers.length}
                                    loading={false}
                                    icon={<WarningIcon fontSize="large" htmlColor={theme.palette.warning.dark} />}
                                    theme={theme}
                                />
                            </Grid>
                        )} */}
                        {publicMetricSubscribedAt &&
                            'totalEarnings' in publicMetricSubscribedAt &&
                            typeof publicMetricSubscribedAt.totalEarnings === 'number' && (
                                <>
                                    <Grid item xs={12} md={6} lg={3}>
                                        <PriceCard
                                            title="Total Earned (gross)"
                                            value={dinero({
                                                amount: Math.trunc(publicMetricSubscribedAt.totalEarnings * 100),
                                                currency: 'USD',
                                            })}
                                            loading={false}
                                            icon={<AttachMoneyIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                            theme={theme}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={3}>
                                        <PriceCard
                                            title="Total Earned (net)"
                                            value={dinero({
                                                amount: Math.trunc(publicMetricSubscribedAt.totalEarnings * 100 * 0.8),
                                                currency: 'USD',
                                            })}
                                            loading={false}
                                            icon={<AttachMoneyIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                            theme={theme}
                                        />
                                    </Grid>
                                </>
                            )}
                        {publicMetricSubscribedAt &&
                            'totalChargebacks' in publicMetricSubscribedAt &&
                            typeof publicMetricSubscribedAt.totalChargebacks === 'number' && (
                                <>
                                    <Grid item xs={12} md={6} lg={3}>
                                        <PriceCard
                                            title="Total Chargebacks (gross)"
                                            value={dinero({
                                                amount: Math.trunc(publicMetricSubscribedAt.totalChargebacks * 100),
                                                currency: 'USD',
                                            })}
                                            loading={false}
                                            icon={<AttachMoneyIcon fontSize="large" htmlColor={theme.palette.error.dark} />}
                                            theme={theme}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={3}>
                                        <PriceCard
                                            title="Total Chargebacks (net)"
                                            value={dinero({
                                                amount: Math.trunc(publicMetricSubscribedAt.totalChargebacks * 100 * 0.8),
                                                currency: 'USD',
                                            })}
                                            loading={false}
                                            icon={<AttachMoneyIcon fontSize="large" htmlColor={theme.palette.error.dark} />}
                                            theme={theme}
                                        />
                                    </Grid>
                                </>
                            )}
                    </Grid>

                    {metricOverview?.type === 'trialLinkTrial' && metricOverview.trialUrl && (
                        <SextforceMetricsTrialLink trialUrl={metricOverview.trialUrl} theme={theme} />
                    )}

                    {metricOverview?.type === 'promoCampaign' && metricOverview.campaignCode && (
                        <SextforceMetricsTrialLink
                            trialUrl={`https://onlyfans.com/${metricOverview.username}/c${metricOverview.campaignCode}`}
                            theme={theme}
                        />
                    )}

                    {metricCountersLoading && (
                        <Box
                            sx={{
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mt: 2,
                                mb: 2,
                            }}
                        >
                            <Stack direction="column" spacing={2} justifyContent="center" alignItems="center">
                                <Typography variant="h5">Loading Counters History...</Typography>
                                <LinearProgress variant="indeterminate" sx={{ width: '200px' }} />
                            </Stack>
                        </Box>
                    )}

                    {!metricCountersLoading && publicMetricCounters && metricOverview?.type === 'promoCampaign' && (
                        <SextforceMetricsCampaignCountersGraph
                            metricId={metricOverview.metricId}
                            promoCampaignCounters={{ metricsDatas: publicMetricCounters }}
                            promoCampaignCountersLoading={false}
                            timezone={timezone}
                            theme={theme}
                        />
                    )}

                    {!metricCountersLoading && publicMetricCounters && metricOverview?.type === 'trialLinkTrial' && (
                        <SextforceMetricsTrialCountersGraph
                            metricId={metricOverview.metricId}
                            trialCounters={{ metricsDatas: publicMetricCounters }}
                            trialCountersLoading={false}
                            timezone={timezone}
                            theme={theme}
                        />
                    )}

                    {metricSubscribedAtLoading && (
                        <Box
                            sx={{
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mt: 2,
                                mb: 2,
                            }}
                        >
                            <Stack direction="column" spacing={2} justifyContent="center" alignItems="center">
                                <Typography variant="h5">Loading Subscribers Data...</Typography>
                                <LinearProgress variant="indeterminate" sx={{ width: '200px' }} />
                            </Stack>
                        </Box>
                    )}

                    {!metricSubscribedAtLoading && publicMetricSubscribedAt && metricOverview?.type === 'promoCampaign' && (
                        <SextforceMetricsCampaignSubscribedAtGraph
                            metricId={''}
                            promoCampaignSubscribedAtVsSales={publicMetricSubscribedAt.subscribersGraph}
                            promoCampaignSubscribedAtVsSalesLoading={false}
                            showEarnings={publicMetricSubscribedAt.totalEarnings ? true : false}
                            showEarningsAsGross={true}
                            showEarningsWithSubscriptions={true}
                            timezone={timezone}
                            theme={theme}
                        />
                    )}

                    {!metricSubscribedAtLoading &&
                        publicMetricSubscribedAt &&
                        publicMetricSubscribedAt.subscribersGraph &&
                        (metricOverview?.type === 'trialLinkTrial' || metricOverview?.type === 'trialLinkPromo') && (
                            <SextforceMetricsTrialSubscribedAtGraph
                                metricId={''}
                                trialSubscribedAtVsSales={publicMetricSubscribedAt.subscribersGraph}
                                trialSubscribedAtVsSalesLoading={false}
                                showEarnings={publicMetricSubscribedAt.totalEarnings ? true : false}
                                showEarningsAsGross={true}
                                showEarningsWithSubscriptions={true}
                                timezone={timezone}
                                theme={theme}
                            />
                        )}
                </Box>
            )}
        </>
    );
};

const CardTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => {
    return (
        <Stack direction="column" spacing={0}>
            <Typography variant="subtitle1" align="right">
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="subtitle2" align="right" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
        </Stack>
    );
};

const SextforceMetricsPublic = () => {
    const params = useParams();
    const theme: Theme = useTheme();

    const timezone: string = moment.tz.guess();

    const { data: publicMetricOverview, isLoading: metricOverviewLoading } = useSextforcePublicMetricOverview(params.metricShareLinkId);

    const totalSubscribers = () => {
        if (!publicMetricOverview) {
            return 0;
        }

        if (!Array.isArray(publicMetricOverview.results)) {
            return publicMetricOverview.results.counters.countSubscribers || 0;
        }

        let total = 0;

        publicMetricOverview.results.forEach(m => {
            if (m.counters && 'countSubscribers' in m.counters && typeof m.counters.countSubscribers === 'number') {
                total += m.counters.countSubscribers;
            }
        });

        return total;
    };

    const totalClicks = () => {
        if (!publicMetricOverview) {
            return 0;
        }

        if (!Array.isArray(publicMetricOverview.results)) {
            return publicMetricOverview.results.counters.countTransitions || 0;
        }

        let total = 0;

        publicMetricOverview.results.forEach(m => {
            if (m.counters && 'countTransitions' in m.counters && typeof m.counters.countTransitions === 'number') {
                total += m.counters.countTransitions;
            }
        });

        return total;
    };

    const totalClaims = () => {
        if (!publicMetricOverview) {
            return 0;
        }

        if (!Array.isArray(publicMetricOverview.results)) {
            return publicMetricOverview.results.counters.claimsCount || 0;
        }

        let total = 0;

        publicMetricOverview.results.forEach(m => {
            if (m.counters && 'claimsCount' in m.counters && typeof m.counters.claimsCount === 'number') {
                total += m.counters.claimsCount;
            }
        });

        return total;
    };

    const totalSubscribe = () => {
        if (!publicMetricOverview) {
            return 0;
        }

        if (!Array.isArray(publicMetricOverview.results)) {
            return publicMetricOverview.results.counters.subscribeCounts || 0;
        }

        let total = 0;

        publicMetricOverview.results.forEach(m => {
            if (m.counters && 'subscribeCounts' in m.counters && typeof m.counters.subscribeCounts === 'number') {
                total += m.counters.subscribeCounts;
            }
        });

        return total;
    };

    // Set global MomentJS locale
    moment.locale('en-gb');

    return (
        <Box
            sx={{
                paddingTop: 2,
                paddingBottom: 2,
                paddingLeft: 2,
                paddingRight: 2,
            }}
        >
            {metricOverviewLoading && (
                <Box
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 2,
                    }}
                >
                    <Stack direction="column" spacing={2} justifyContent="center" alignItems="center">
                        <Typography variant="h5">Loading...</Typography>
                        <LinearProgress variant="indeterminate" sx={{ width: '200px' }} />
                    </Stack>
                </Box>
            )}

            {/** Single Metric */}
            {params.metricShareLinkId && publicMetricOverview && !Array.isArray(publicMetricOverview?.results) && (
                <PublicMetricData
                    metricShareLinkId={params.metricShareLinkId}
                    metricOverviewLoading={metricOverviewLoading}
                    metricOverview={publicMetricOverview?.results}
                    timezone={timezone}
                />
            )}

            {params.metricShareLinkId &&
                !metricOverviewLoading &&
                publicMetricOverview &&
                publicMetricOverview.type === 'metricsGroup' &&
                Array.isArray(publicMetricOverview.results) && (
                    <>
                        <Typography variant="h5">Metrics Group ({publicMetricOverview.results.length.toLocaleString()})</Typography>
                        {publicMetricOverview.name && <Typography variant="h6">{publicMetricOverview.name}</Typography>}
                        {publicMetricOverview.createdAt && (
                            <Typography variant="subtitle1">{`Created at: ${moment(publicMetricOverview.createdAt)
                                .tz(timezone)
                                .format('LL')}`}</Typography>
                        )}

                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            Summary
                        </Typography>

                        <Grid container spacing={1} flexGrow={0} justifyContent="center" sx={{ marginBottom: theme.spacing(2) }}>
                            <Grid item xs={12} md={6} lg={3}>
                                <OverviewCard
                                    title={<CardTitle title="Total Subscribed" subtitle="Campaigns" />}
                                    value={totalSubscribers()}
                                    loading={false}
                                    icon={<SubscriptionsIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                    theme={theme}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <OverviewCard
                                    title={<CardTitle title="Total Clicked" subtitle="Campaigns" />}
                                    value={totalClicks()}
                                    loading={false}
                                    icon={<MouseIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                    theme={theme}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <OverviewCard
                                    title={<CardTitle title="Total Claimed" subtitle="Trials" />}
                                    value={totalClaims()}
                                    loading={false}
                                    icon={<SubscriptionsIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                    theme={theme}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <OverviewCard
                                    title={<CardTitle title="Total Clicked" subtitle="Trials" />}
                                    value={totalSubscribe()}
                                    loading={false}
                                    icon={<MouseIcon fontSize="large" htmlColor={theme.palette.success.dark} />}
                                    theme={theme}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ mb: 2 }} />

                        {publicMetricOverview.results.map((metric, index) => (
                            <React.Fragment key={index}>
                                <PublicMetricData
                                    metricShareLinkId={params.metricShareLinkId!}
                                    metricOverviewLoading={false}
                                    metricOverview={metric}
                                    timezone={timezone}
                                />

                                {Array.isArray(publicMetricOverview.results) && index < publicMetricOverview.results.length - 1 && (
                                    <Divider sx={{ mb: 2 }} />
                                )}
                            </React.Fragment>
                        ))}
                    </>
                )}
        </Box>
    );
};

export default SextforceMetricsPublic;
